// src/pages/crm/EditLeadD.js
import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "../../components/Header";
import API_BASE_URL from "../../config";
import Select from "react-select";
import statesWithCities from "../../data/statesWithCities";
import { Modal, Button } from "react-bootstrap";

const EditLeadD = () => {
  const { id } = useParams();

  const [form, setForm] = useState({
    fullName: "",
    company: "",
    gstNumber: "",
    designation: "",
    mobile: "",
    altMobile: "",
    email: "",
    altEmail: "",
    gender: "",
    address: "",
    state: "",
    city: "",
    pincode: "",
    landmark: "",
    website: "",
    linkedin: "",
    source: "",
    funnelStage: "",
    assignedTo: "",
    priority: "",
    profilePic: null,
    businessCategory: "",
  });

  const [leadSources, setLeadSources] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newSource, setNewSource] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [showSourceModal, setShowSourceModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const funnelStages = [
    "New",
    "Contacted",
    "Follow-Up",
    "Qualified",
    "Proposal",
    "Lost",
    "Won",
  ];

  useEffect(() => {
    document.title = "Edit Lead | Webyappy";
  }, []);

  useEffect(() => {
    setIsLoading(true); // Start loading
    fetch(`${API_BASE_URL}/crm/get_lead_details/${id}`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        if (data?.success && data.data) {
          const lead = data.data;
          setForm((prev) => ({
            ...prev,
            fullName: lead.full_name || "",
            company: lead.company || "",
            gstNumber: lead.gst_number || "",
            designation: lead.designation || "",
            mobile: lead.mobile || "",
            altMobile: lead.alt_mobile || "",
            email: lead.email || "",
            altEmail: lead.alt_email || "",
            gender: lead.gender || "",
            address: lead.address || "",
            state: lead.state || "",
            city: lead.city || "",
            pincode: lead.pincode || "",
            landmark: lead.landmark || "",
            website: lead.website || "",
            linkedin: lead.linkedin || "",
            source: lead.source || "",
            funnelStage: lead.funnel_stage || "",
            assignedTo: lead.assigned_to || "",
            priority: lead.priority || "",
            businessCategory: lead.business_category || "",
            profilePic: null,
          }));
        }
      })
      .finally(() => {
        setIsLoading(false); // Done loading
      });
  }, [id]);

  useEffect(() => {
    fetch(`${API_BASE_URL}/crm/get_lead_sources`, { credentials: "include" })
      .then((res) => res.json())
      .then((data) => setLeadSources(data));

    fetch(`${API_BASE_URL}/auth/get_users_list`, { credentials: "include" })
      .then((res) => res.json())
      .then((data) => setUsers(data.filter((u) => u.is_active)));

    fetch(`${API_BASE_URL}/crm/get_business_categories`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => setCategories(data));
  }, []);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "profilePic") {
      const file = files[0];
      if (
        file &&
        ["image/jpeg", "image/jpg", "image/png"].includes(file.type) &&
        file.size <= 2 * 1024 * 1024
      ) {
        setForm({ ...form, profilePic: file });
      }
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const validateDuplicates = () => {
    const errors = [];
    if (form.mobile && form.altMobile && form.mobile === form.altMobile)
      errors.push(
        "Mobile number and Alternate Mobile number should be different.",
      );
    if (form.email && form.altEmail && form.email === form.altEmail)
      errors.push("Email ID and Alternate Email ID should be different.");
    return errors;
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i;
    return emailRegex.test(email);
  };

  const checkDuplicate = async (field, value) => {
    try {
      const res = await fetch(`${API_BASE_URL}/crm/check_duplicate_lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ field, value }),
        credentials: "include",
      });
      const data = await res.json();
      return data.exists;
    } catch (err) {
      console.error("Duplicate check error:", err);
      return false;
    }
  };

  const checkFieldDuplicate = async (field, value) => {
    if (!value) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
      return;
    }

    const isEmailField = field === "email" || field === "altEmail";
    if (isEmailField && !isValidEmail(value)) {
      setErrors((prev) => ({
        ...prev,
        [field]: "Enter a valid email address",
      }));
      return;
    }

    const exists = await checkDuplicate(field, value);
    setErrors((prev) => ({
      ...prev,
      [field]: exists ? `${field} already exists` : "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const requiredErrors = [];
    if (!form.fullName.trim()) requiredErrors.push("Full Name is required.");
    if (!form.company.trim()) requiredErrors.push("Company Name is required.");
    if (!form.gender.trim()) requiredErrors.push("Gender is required.");
    if (!form.mobile.trim()) requiredErrors.push("Mobile Number is required.");
    if (!form.email.trim()) requiredErrors.push("Email ID is required.");
    if (!form.assignedTo) requiredErrors.push("Assigned To is required.");

    const duplicateErrors = validateDuplicates();

    if (requiredErrors.length || duplicateErrors.length) {
      alert([...requiredErrors, ...duplicateErrors].join("\n"));
      return;
    }

    setIsSubmitting(true);

    const normalizedForm = {
      ...form,
      state: typeof form.state === "object" ? form.state.value : form.state,
      city: typeof form.city === "object" ? form.city.value : form.city,
      businessCategory:
        typeof form.businessCategory === "object"
          ? form.businessCategory.value
          : form.businessCategory,
      funnelStage:
        typeof form.funnelStage === "object"
          ? form.funnelStage.value
          : form.funnelStage,
      assignedTo:
        typeof form.assignedTo === "object"
          ? form.assignedTo.value
          : form.assignedTo,
    };

    const data = new FormData();
    Object.entries(normalizedForm).forEach(([key, value]) => {
      if (key === "profilePic" && value === null) return;
      data.append(key, value ?? "");
    });

    try {
      console.log("Submitting lead update for ID:", id);

      const res = await fetch(`${API_BASE_URL}/crm/update_lead/${id}`, {
        method: "POST",
        body: data,
        credentials: "include",
      });

      const result = await res.json();
      console.log("Update result:", result); // 🧪 Debug line

      if (result.status === "success") {
        setShowSuccessModal(true);
        setErrors({});
      } else {
        alert(result.message || "Failed to update lead.");
      }
    } catch (err) {
      console.error("Server error:", err);
      alert("Something went wrong. Try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {isLoading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(255,255,255,0.7)",
            zIndex: 9999,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      )}

      <Header />
      <div style={{ paddingTop: "70px", paddingBottom: "70px" }}>
        <div className="container py-3">
          <div className="d-flex align-items-center justify-content-between flex-wrap flex-sm-nowrap mb-2">
            {/* Left: Breadcrumb */}
            <ul className="breadcrumb breadcrumb-transparent breadcrumb-dot font-weight-bold p-0 mb-0">
              <li className="breadcrumb-item">
                <Link to="/home" className="text-muted">
                  Home
                </Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/pages/crm/editleads" className="text-muted">
                  Edit Leads
                </Link>
              </li>
              <li className="breadcrumb-item">
                <span className="text-dark font-weight-bold">Lead Details</span>
              </li>
            </ul>

            {/* Right: Back Button */}
            <div className="mt-3 mt-sm-0">
              <Link to="/pages/crm/editleads" className="btn btn-light">
                <i className="fas fa-arrow-left"></i> Back
              </Link>
            </div>
          </div>

          <div className="card card-custom shadow-sm">
            <div className="card-body">
              <h5 className="font-weight-bold mb-4">Edit Lead Information</h5>
              <form onSubmit={handleSubmit} encType="multipart/form-data">
                <div className="row">
                  <div className="form-group col-md-4 mb-3">
                    <label className="small">
                      Full Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="fullName"
                      placeholder="Enter full name"
                      value={form.fullName}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group col-md-4 mb-3">
                    <label className="small">
                      Company Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="company"
                      placeholder="Enter company name"
                      value={form.company}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group col-md-4 mb-3">
                    <label className="small">GST No.</label>
                    <input
                      type="text"
                      className="form-control"
                      name="gstNumber"
                      placeholder="Enter GST No (Optional)"
                      value={form.gstNumber}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group col-md-4 mb-3">
                    <label className="small">Designation</label>
                    <input
                      type="text"
                      className="form-control"
                      name="designation"
                      placeholder="Optional"
                      value={form.designation}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group col-md-4 mb-3">
                    <label className="small">
                      Mobile Number <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="mobile"
                      placeholder="Enter mobile number"
                      value={form.mobile}
                      onChange={handleChange}
                      onBlur={() => checkFieldDuplicate("mobile", form.mobile)}
                    />
                    {errors.mobile && (
                      <small className="text-danger">{errors.mobile}</small>
                    )}
                  </div>

                  <div className="form-group col-md-4 mb-3">
                    <label className="small">Alt. Mobile Number</label>
                    <input
                      type="text"
                      className="form-control"
                      name="altMobile"
                      placeholder="Enter mobile number"
                      value={form.altMobile}
                      onChange={handleChange}
                      onBlur={() =>
                        checkFieldDuplicate("altMobile", form.altMobile)
                      }
                    />
                    {errors.altMobile && (
                      <small className="text-danger">{errors.altMobile}</small>
                    )}
                  </div>

                  <div className="form-group col-md-4 mb-3">
                    <label className="small">
                      Email ID <span className="text-danger">*</span>
                    </label>
                    <input
                      type="email"
                      className="form-control"
                      name="email"
                      placeholder="Enter email address"
                      value={form.email}
                      onChange={handleChange}
                      onBlur={() => checkFieldDuplicate("email", form.email)}
                    />
                    {errors.email && (
                      <small className="text-danger">{errors.email}</small>
                    )}
                  </div>

                  <div className="form-group col-md-4 mb-3">
                    <label className="small">Alt. Email ID</label>
                    <input
                      type="email"
                      className="form-control"
                      name="altEmail"
                      placeholder="Enter email address"
                      value={form.altEmail}
                      onChange={handleChange}
                      onBlur={() =>
                        checkFieldDuplicate("altEmail", form.altEmail)
                      }
                    />
                    {errors.altEmail && (
                      <small className="text-danger">{errors.altEmail}</small>
                    )}
                  </div>

                  <div className="form-group col-md-4 mb-3">
                    <label className="small">Upload Profile Image</label>
                    <input
                      type="file"
                      className="form-control"
                      name="profilePic"
                      accept="image/*"
                      onChange={handleChange}
                    />
                    <p
                      className="form-text text-danger"
                      style={{ fontSize: "9px" }}
                    >
                      Allowed formats: jpg, png. Max size: 2MB
                    </p>
                  </div>

                  <div className="form-group col-md-4 mb-3">
                    <label className="small">
                      Gender <span className="text-danger">*</span>
                    </label>
                    <div className="d-flex align-items-center mt-2">
                      {["Male", "Female", "Other"].map((gender) => (
                        <div
                          className="form-check form-check-inline mr-4"
                          key={gender}
                        >
                          <input
                            className="form-check-input"
                            type="radio"
                            name="gender"
                            id={`gender-${gender}`}
                            value={gender}
                            checked={form.gender === gender}
                            onChange={handleChange}
                          />
                          <label
                            className="form-check-label"
                            htmlFor={`gender-${gender}`}
                          >
                            {gender}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="form-group col-md-4 mb-3">
                    <label className="small">Business Category</label>
                    <Select
                      className="basic-single shadow-sm"
                      classNamePrefix="select"
                      placeholder="Select Category"
                      options={[
                        ...categories.map((cat) => ({
                          label: cat.name,
                          value: cat.name,
                        })),
                        { label: "+ Add New", value: "add_new" },
                      ]}
                      value={
                        form.businessCategory
                          ? {
                              label: form.businessCategory,
                              value: form.businessCategory,
                            }
                          : null
                      }
                      onChange={(option) => {
                        if (option?.value === "add_new") {
                          setShowCategoryModal(true);
                        } else {
                          setForm((prev) => ({
                            ...prev,
                            businessCategory: option?.value || "",
                          }));
                        }
                      }}
                      isClearable
                      isSearchable
                    />
                  </div>

                  <div className="form-group col-md-4 mb-3">
                    <label className="small">Address</label>
                    <input
                      type="text"
                      className="form-control"
                      name="address"
                      placeholder="Enter full address"
                      value={form.address}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group col-md-4 mb-3">
                    <label className="small">State</label>
                    <Select
                      className="basic-single shadow-sm"
                      classNamePrefix="select"
                      placeholder="Select State"
                      options={Object.keys(statesWithCities).map((state) => ({
                        label: state,
                        value: state,
                      }))}
                      value={
                        form.state
                          ? { label: form.state, value: form.state }
                          : null
                      }
                      onChange={(option) =>
                        setForm((prev) => ({
                          ...prev,
                          state: option?.value || "",
                          city: "", // Reset city
                        }))
                      }
                      isClearable
                    />
                  </div>

                  <div className="form-group col-md-4 mb-3">
                    <label className="small">City</label>
                    <Select
                      className="basic-single shadow-sm"
                      classNamePrefix="select"
                      placeholder={
                        form.state ? "Select City" : "Select state first"
                      }
                      options={
                        form.state
                          ? statesWithCities[form.state].map((city) => ({
                              label: city,
                              value: city,
                            }))
                          : []
                      }
                      value={
                        form.city
                          ? { label: form.city, value: form.city }
                          : null
                      }
                      onChange={(option) =>
                        setForm((prev) => ({
                          ...prev,
                          city: option?.value || "",
                        }))
                      }
                      isDisabled={!form.state}
                      isClearable
                    />
                  </div>

                  <div className="form-group col-md-4 mb-3">
                    <label className="small">Pincode</label>
                    <input
                      type="text"
                      className="form-control"
                      name="pincode"
                      placeholder="Enter pincode"
                      value={form.pincode}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group col-md-4 mb-3">
                    <label className="small">Landmark</label>
                    <input
                      type="text"
                      className="form-control"
                      name="landmark"
                      placeholder="Nearby landmark (optional)"
                      value={form.landmark}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group col-md-4 mb-3">
                    <label className="small">Website</label>
                    <input
                      type="text"
                      className="form-control"
                      name="website"
                      placeholder="https://example.com"
                      value={form.website}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group col-md-4 mb-3">
                    <label className="small">LinkedIn</label>
                    <input
                      type="text"
                      className="form-control"
                      name="linkedin"
                      placeholder="https://linkedin.com/in/username"
                      value={form.linkedin}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group col-md-4 mb-3">
                    <label className="small">Lead Source</label>
                    <Select
                      className="basic-single shadow-sm"
                      classNamePrefix="select"
                      placeholder="Select Lead Source"
                      options={[
                        ...leadSources.map((source) => ({
                          label: source.name,
                          value: source.name,
                        })),
                        { label: "+ Add New", value: "add_new" },
                      ]}
                      value={
                        form.source
                          ? { label: form.source, value: form.source }
                          : null
                      }
                      onChange={(option) => {
                        if (option?.value === "add_new") {
                          setShowSourceModal(true);
                        } else {
                          setForm((prev) => ({
                            ...prev,
                            source: option?.value || "",
                          }));
                        }
                      }}
                      isClearable
                    />
                  </div>
                  <div className="form-group col-md-4 mb-3">
                    <label className="small">Assigned To</label>
                    <Select
                      className="basic-single shadow-sm"
                      classNamePrefix="select"
                      placeholder="Select User"
                      options={users.map((user) => ({
                        label: user.name,
                        value: user.id,
                      }))}
                      value={
                        form.assignedTo
                          ? {
                              label:
                                users.find((u) => u.id == form.assignedTo)
                                  ?.name || "",
                              value: form.assignedTo,
                            }
                          : null
                      }
                      onChange={(option) =>
                        setForm((prev) => ({
                          ...prev,
                          assignedTo: option?.value || "",
                        }))
                      }
                      isClearable
                      isSearchable
                    />
                  </div>

                  <div className="form-group col-md-4 mb-3">
                    <label className="small">Funnel Stage</label>
                    <select
                      name="funnelStage"
                      className="form-control shadow-sm"
                      value={form.funnelStage}
                      onChange={handleChange}
                    >
                      <option value="">Select Funnel Stage</option>
                      {funnelStages.map((stage) => (
                        <option key={stage} value={stage}>
                          {stage}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group col-md-4 mb-3">
                    <label className="small">Priority</label>
                    <select
                      name="priority"
                      className="form-control shadow-sm"
                      value={form.priority}
                      onChange={handleChange}
                    >
                      <option value="">Select Priority</option>
                      <option value="High">High</option>
                      <option value="Medium">Medium</option>
                      <option value="Low">Low</option>
                    </select>
                  </div>
                </div>

                <div className="text-center mt-4">
                  <button
                    type="submit"
                    className="btn btn-primary font-weight-bold px-5 w-100"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm mr-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Updating...
                      </>
                    ) : (
                      <>Update Lead</>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <Modal
        show={showSuccessModal}
        onHide={() => setShowSuccessModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-success">
            <i className="fas fa-check-circle mr-2"></i> Lead Updated
            Successfully!
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <p className="mb-4">What would you like to do next?</p>
          <div className="d-flex flex-wrap justify-content-center gap-2">
            <Link
              to="/pages/crm/viewleads"
              className="btn btn-primary font-weight-bold"
            >
              <i className="fas fa-eye mr-1"></i> View Leads
            </Link>
            <Link
              to="/pages/crm/editleads"
              className="btn btn-warning font-weight-bold text-white"
            >
              <i className="fas fa-edit mr-1"></i> Edit More Leads
            </Link>
            <Link
              to="/pages/crm/CRM"
              className="btn btn-secondary font-weight-bold"
            >
              <i className="fas fa-th-large mr-1"></i> CRM
            </Link>
          </div>
        </Modal.Body>
      </Modal>
      {/* Add Source Modal */}
      <Modal show={showSourceModal} onHide={() => setShowSourceModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Lead Source</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <input
            type="text"
            className="form-control"
            placeholder="Enter new lead source"
            value={newSource}
            onChange={(e) => setNewSource(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSourceModal(false)}>
            Cancel
          </Button>
          <Button
  variant="primary"
  onClick={async () => {
    if (!newSource.trim()) return;

    try {
      const res = await fetch(`${API_BASE_URL}/crm/add_lead_source`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newSource }),
        credentials: "include",
      });

      const result = await res.json();

      if (result.status === "success") {
        const newOption = {
          label: newSource,
          value: newSource,
        };
        setLeadSources((prev) => [...prev, { name: newSource }]);
        setForm((prev) => ({ ...prev, source: newSource }));
        setNewSource("");
        setShowSourceModal(false);
      } else {
        alert("Failed to add lead source.");
      }
    } catch (error) {
      console.error("Error adding lead source:", error);
      alert("Something went wrong.");
    }
  }}
>
  Save
</Button>

        </Modal.Footer>
      </Modal>

      {/* Add Category Modal */}
      <Modal
        show={showCategoryModal}
        onHide={() => setShowCategoryModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Add New Business Category</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <input
            type="text"
            className="form-control"
            placeholder="Enter new business category"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCategoryModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={async () => {
              if (!newCategory.trim()) return;
              const res = await fetch(
                `${API_BASE_URL}/crm/add_business_category`,
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ name: newCategory }),
                  credentials: "include",
                },
              );
              const result = await res.json();
              if (result.status === "success") {
                setCategories([...categories, { name: newCategory }]);
                setForm((prev) => ({ ...prev, businessCategory: newCategory }));
                setNewCategory("");
                setShowCategoryModal(false);
              } else {
                alert("Failed to add business category.");
              }
            }}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EditLeadD;
